import React, { useEffect, useState } from "react";
import { ComponentPalette } from "@/components/editor/ComponentPaletteSimple";
import { ResponsiveEditorCanvas } from "@/components/editor/ResponsiveEditorCanvas";
import { PropertiesPanel } from "@/components/editor/PropertiesPanel";
import { useEditorStore } from "@/stores/editor/editor-store";
import { ComponentType } from "@/types";
import { Button } from "@/components/ui/button";

export const WorkflowTestPage: React.FC = () => {
  const [testResults, setTestResults] = useState<string[]>([]);
  const {
    components,
    selectedComponents,
    addComponent,
    selectComponent,
    updateComponent,
    clearSelection,
    setComponents,
  } = useEditorStore();

  const log = (message: string) => {
    console.log(message);
    setTestResults((prev) => [
      ...prev,
      `${new Date().toLocaleTimeString()}: ${message}`,
    ]);
  };

  const runWorkflowTests = () => {
    setTestResults([]);
    log("🚀 Starting AI workflow tests...");

    // Clear existing components
    setComponents([]);
    clearSelection();
    log("✅ Cleared existing components and selection");

    // Test 1: Add a component programmatically (simulating AI creation)
    const testComponent = {
      projectId: "test-project",
      pageId: "test-page",
      name: "Test Text Component",
      type: ComponentType.TEXT,
      properties: {
        content: "Hello World",
        fontSize: 16,
        fontWeight: "normal",
        textAlign: "left",
      },
      position: { x: 100, y: 100, z: 1 },
      responsivePosition: {
        strategy: "absolute" as const,
      },
      size: { width: 200, height: 40 },
      style: {
        color: "#000000",
        backgroundColor: "transparent",
      },
      parentId: undefined,
      children: [],
      order: 0,
      locked: false,
      hidden: false,
      version: 1,
      createdBy: "test-user",
      lastModifiedBy: "test-user",
      lastModifiedAt: new Date(),
      isDeleted: false,
      deletedAt: undefined,
    };

    addComponent(testComponent);
    log("✅ Added test component via addComponent()");

    setTimeout(() => {
      const currentComponents = useEditorStore.getState().components;
      const currentSelection = useEditorStore.getState().selectedComponents;

      if (currentComponents.length > 0) {
        log(
          `✅ Component created successfully. Count: ${currentComponents.length}`,
        );

        if (currentSelection.length > 0) {
          log(
            `✅ Component auto-selected. Selected: ${currentSelection.length}`,
          );

          // Test 2: Update component properties
          const componentId = currentComponents[0].id;
          updateComponent(componentId, {
            properties: {
              ...currentComponents[0].properties,
              content: "Updated Content via Property Panel",
            },
          });
          log("✅ Updated component properties");

          setTimeout(() => {
            const updatedComponent = useEditorStore
              .getState()
              .getComponent(componentId);
            if (
              updatedComponent?.properties.content ===
              "Updated Content via Property Panel"
            ) {
              log("✅ Property update verified successfully");
            } else {
              log("❌ Property update failed");
            }

            // Test 3: Test manual selection
            clearSelection();
            setTimeout(() => {
              if (useEditorStore.getState().selectedComponents.length === 0) {
                log("✅ Selection cleared successfully");

                selectComponent(componentId);
                setTimeout(() => {
                  if (
                    useEditorStore.getState().selectedComponents.length === 1
                  ) {
                    log("✅ Manual selection working");
                    log("🎉 All workflow tests completed successfully!");
                  } else {
                    log("❌ Manual selection failed");
                  }
                }, 100);
              } else {
                log("❌ Clear selection failed");
              }
            }, 100);
          }, 100);
        } else {
          log("❌ Component not auto-selected");
        }
      } else {
        log("❌ Component creation failed");
      }
    }, 100);
  };

  return (
    <div className="flex flex-col h-screen">
      {/* Test Controls */}
      <div className="p-4 bg-gray-100 border-b">
        <h1 className="text-2xl font-bold mb-4">
          AI Workflow Test Page
        </h1>
        <div className="flex gap-4 mb-4">
          <Button onClick={runWorkflowTests}>Run Workflow Tests</Button>
          <Button variant="outline" onClick={() => setTestResults([])}>
            Clear Logs
          </Button>
          <div className="flex items-center gap-2 text-sm">
            <span>Components: {components.length}</span>
            <span>Selected: {selectedComponents.length}</span>
          </div>
        </div>

        {/* Test Results */}
        {testResults.length > 0 && (
          <div className="bg-white rounded border p-3 max-h-32 overflow-y-auto">
            <h3 className="font-semibold mb-2">Test Results:</h3>
            {testResults.map((result, index) => (
              <div key={index} className="text-sm font-mono">
                {result}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Editor Layout */}
        <div className="flex-1 flex">
          {/* Left Sidebar - Component Palette */}
          <div className="w-80 bg-card border-r border-border">
            <ComponentPalette />
          </div>

          {/* Main Editor Area */}
          <div className="flex-1 bg-muted/20">
            <ResponsiveEditorCanvas
              viewport={{
                id: 'desktop',
                name: 'Desktop',
                width: 1920,
                height: 1080,
                icon: '🖥️',
                mediaQuery: '(min-width: 1024px)',
                containerQuery: '(min-width: 1024px)',
                isCustom: false,
                order: 3,
              }}
              components={components}
              isActive={true}
              isVisible={true}
              scale={1}
              selectedComponents={selectedComponents}
              onComponentSelect={(id) => selectComponent(id)}
              onCanvasClick={() => {}}
              onComponentUpdate={(id, updates) => updateComponent(id, updates)}
            />
          </div>

          {/* Right Sidebar - Properties Panel */}
          <div className="w-80 bg-card border-l border-border">
            <PropertiesPanel />
          </div>
        </div>

      {/* Instructions */}
      <div className="p-4 bg-gray-50 border-t text-sm">
        <h3 className="font-semibold mb-2">Manual Testing Instructions:</h3>
        <ol className="list-decimal list-inside space-y-1">
          <li>
            Click "Run Workflow Tests" to test programmatic component creation
            and selection
          </li>
          <li>Try dragging components from the palette to the canvas</li>
          <li>Click on components in the canvas to select them</li>
          <li>Modify properties in the Properties Panel on the right</li>
          <li>Verify that changes are reflected in real-time</li>
        </ol>
      </div>
    </div>
  );
};
